import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../../common/SEOHead";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloud,
  faCode,
  faDatabase,
  faLaptopCode,
  faFilePdf,
  faCogs,
  faUser,
  faChartLine,
  faArrowRight,
  faCheckCircle,
  faUsers,
  faClock,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import { baseUrl } from "../../config";
import { trackEvent } from "../../components/trackingUtils";

import "./ServicesMaster.css";

const ServicesMaster = ({ services }) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    const trackPageLoad = async () => {
      try {
        await trackEvent("PageLoad", "", "ServicesMaster");
      } catch (error) {
        console.error("Error tracking page load:", error);
      } finally {
        setIsLoading(false);
      }
    };

    trackPageLoad();
  }, []);

  // Scroll progress indicator
  useEffect(() => {
    const updateScrollProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min(scrollTop / docHeight, 1) * 100;
      setScrollProgress(progress);
    };

    const handleScroll = () => {
      updateScrollProgress();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getServiceIcon = (icons) => {
    switch (icons) {
      case "Development":
        return faCode;
      case "Cloud":
        return faCloud;
      case "Consulting":
        return faUser;
      case "website building":
        return faLaptopCode;
      case "PDF":
        return faFilePdf;
      case "Data":
        return faDatabase;
      case "Analytics":
        return faChartLine;
      default:
        return faCogs;
    }
  };

  const getServiceColor = (icon) => {
    const colors = {
      Development: "from-blue-500 to-indigo-600",
      Cloud: "from-cyan-500 to-blue-600",
      Consulting: "from-purple-500 to-pink-600",
      "website building": "from-green-500 to-emerald-600",
      PDF: "from-red-500 to-rose-600",
      Data: "from-orange-500 to-amber-600",
      Analytics: "from-teal-500 to-cyan-600",
      default: "from-gray-500 to-slate-600",
    };
    return colors[icon] || colors.default;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="text-center space-y-4">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-[#E5C558] mb-6"></div>
          <p className="text-xl text-gray-600 font-medium">
            Loading our services...
          </p>
        </div>
      </div>
    );
  }

  const title = "Our Services - Professional Solutions | GeniusNexa";
  const description =
    "Discover GeniusNexa's comprehensive range of technology services including cloud solutions, custom development, data management, PDF conversion, and website building. Transform your business with our expert team.";
  const keywords =
    "GeniusNexa services, cloud services, software development, data management, PDF conversion, website development, technology solutions, business automation, digital transformation, consulting services, custom applications, web development, mobile apps, data analytics";

  return (
    <>
      {/* Enhanced SEO */}
      <SEOHead
        title={title}
        description={description}
        keywords={keywords}
        image={`${baseUrl}/images/ourservices.png`}
        url={`${baseUrl}/services`}
        type="website"
        section="Services"
      />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "GeniusNexa",
          url: baseUrl,
          logo: `${baseUrl}/images/logo.png`,
          description: description,
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "GeniusNexa Services",
            itemListElement:
              services?.map((service, index) => ({
                "@type": "Offer",
                position: index + 1,
                itemOffered: {
                  "@type": "Service",
                  name: service.title,
                  description: service.description,
                  url: `${baseUrl}/services/${service.url}`,
                },
              })) || [],
          },
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "+91 9944544603",
            contactType: "Customer Service",
            availableLanguage: "English",
          },
          areaServed: "Global",
        })}
      </script>

      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <div
          className="h-1 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] transition-all duration-150"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <defs>
                <pattern
                  id="hero-dots"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <circle cx="10" cy="10" r="1" fill="#E5C558" />
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#hero-dots)" />
            </svg>
          </div>

          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gradient-to-br from-[#E5C558]/20 to-yellow-400/10 rounded-full animate-pulse"></div>
            <div className="absolute top-3/4 right-1/4 w-24 h-24 bg-gradient-to-br from-blue-400/15 to-[#E5C558]/10 rounded-full animate-pulse delay-1000"></div>
            <div className="absolute bottom-1/4 left-1/3 w-20 h-20 bg-gradient-to-br from-purple-400/10 to-[#E5C558]/10 rounded-full animate-pulse delay-2000"></div>
          </div>

          <div className="container mx-auto px-4 lg:px-6 relative z-10">
            <div className="text-center max-w-6xl mx-auto space-y-8">
              <div className="inline-flex items-center px-6 py-3 bg-[#E5C558]/20 backdrop-blur-sm rounded-full text-[#E5C558] font-semibold mb-6 border border-[#E5C558]/30">
                <FontAwesomeIcon icon={faCogs} className="mr-2" />
                Professional Services Portfolio
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight tracking-tight">
                Our
                <span className="block text-[#E5C558] text-3xl md:text-4xl lg:text-5xl font-normal mt-2">
                  Expert Services
                </span>
              </h1>

              <p className="text-xl lg:text-2xl text-gray-600 mb-12 leading-relaxed max-w-4xl mx-auto font-medium">
                Transform your business with our comprehensive suite of
                technology services. From cloud solutions to custom development,
                we deliver excellence that drives results.
              </p>

              {/* Key Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8 max-w-4xl mx-auto mb-12">
                <div className="text-center group">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#E5C558] to-[#d4b54d] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <FontAwesomeIcon
                      icon={faUsers}
                      className="text-white text-xl"
                    />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    500+
                  </div>
                  <div className="text-gray-600 text-sm font-medium">
                    Happy Clients
                  </div>
                </div>

                <div className="text-center group">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="text-white text-xl"
                    />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    99%
                  </div>
                  <div className="text-gray-600 text-sm font-medium">
                    Success Rate
                  </div>
                </div>

                <div className="text-center group">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <FontAwesomeIcon
                      icon={faClock}
                      className="text-white text-xl"
                    />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    24/7
                  </div>
                  <div className="text-gray-600 text-sm font-medium">
                    Support
                  </div>
                </div>

                <div className="text-center group">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <FontAwesomeIcon
                      icon={faGlobe}
                      className="text-white text-xl"
                    />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    Global
                  </div>
                  <div className="text-gray-600 text-sm font-medium">Reach</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-6 justify-center pt-4">
                <a
                  href="#services"
                  className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-[#0b1220] font-semibold rounded-full hover:shadow-xl hover:shadow-[#E5C558]/50 transition-all duration-300 hover:scale-105 focus:ring-4 focus:ring-[#E5C558]/30"
                  aria-label="Explore our services"
                >
                  Explore Services
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="ml-2 group-hover:translate-x-1 transition-transform"
                  />
                </a>

                <Link
                  to="/contact-us"
                  className="group inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-100 hover:border-gray-400 transition-all duration-300 focus:ring-4 focus:ring-gray-300/30"
                  aria-label="Get free consultation"
                >
                  Get Consultation
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="ml-2 group-hover:translate-x-1 transition-transform"
                  />
                </Link>
              </div>
            </div>
          </div>
        </section>
        {/* Services Grid */}
        <section
          id="services"
          className="py-20 lg:py-28 bg-gradient-to-b from-white via-slate-50 to-gray-50 relative"
        >
          {/* Background Decoration */}
          <div className="absolute inset-0 overflow-hidden opacity-5">
            <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-[#E5C558]/30 to-yellow-400/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-blue-400/30 to-purple-400/20 rounded-full blur-3xl"></div>
          </div>

          <div className="container mx-auto px-4 lg:px-6 relative z-10">
            <div className="text-center mb-16">
              <div className="inline-block px-6 py-3 bg-gradient-to-r from-[#E5C558]/20 to-yellow-400/20 backdrop-blur-sm rounded-full text-[#E5C558] font-semibold mb-6 border border-[#E5C558]/30">
                Our Service Portfolio
              </div>
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Complete Technology Solutions
              </h2>
              <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Choose from our comprehensive range of professional services
                designed to accelerate your business growth and digital
                transformation.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
              {services?.map((service, index) => (
                <div
                  key={index}
                  className="group relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-10 hover:shadow-2xl hover:shadow-black/10 transition-all duration-500 hover:-translate-y-2 border border-white/50 overflow-hidden"
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  {/* Card Background Decoration */}
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#E5C558]/10 to-yellow-400/5 rounded-bl-3xl transform transition-transform duration-500 group-hover:scale-110"></div>

                  {/* Service Icon */}
                  <div className="relative z-10 mb-8">
                    <div
                      className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-br ${getServiceColor(
                        service.icon
                      )} rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}
                    >
                      <FontAwesomeIcon
                        icon={getServiceIcon(service.icon)}
                        className="text-white text-2xl"
                      />
                    </div>
                  </div>

                  {/* Service Content */}
                  <div className="relative z-10 text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-[#E5C558] transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-8 leading-relaxed text-base">
                      {service.description}
                    </p>

                    {/* Service Features */}
                    <div className="space-y-3 mb-8">
                      <div className="flex items-center text-sm text-gray-600">
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="text-[#E5C558] mr-3 flex-shrink-0"
                        />
                        Professional Implementation
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="text-[#E5C558] mr-3 flex-shrink-0"
                        />
                        24/7 Support & Maintenance
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="text-[#E5C558] mr-3 flex-shrink-0"
                        />
                        Scalable Solutions
                      </div>
                    </div>

                    {/* CTA Button */}
                    <Link
                      to={`/services/${service.url}`}
                      className="group/btn inline-flex items-center justify-center w-full px-6 py-3 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-[#0b1220] font-semibold rounded-full hover:shadow-lg hover:shadow-[#E5C558]/30 transition-all duration-300 hover:scale-105 focus:ring-4 focus:ring-[#E5C558]/30"
                      onClick={() =>
                        trackEvent(
                          "ServiceClick",
                          service.url,
                          "ServicesMaster"
                        )
                      }
                      aria-label={`Learn more about ${service.title} services`}
                    >
                      Learn More
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="ml-2 group-hover/btn:translate-x-1 transition-transform"
                      />
                    </Link>
                  </div>

                  {/* Hover Effect Overlay */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br from-[#E5C558]/5 to-yellow-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl ${
                      hoveredCard === index ? "opacity-100" : ""
                    }`}
                  ></div>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* Why Choose Us Section */}
        <section className="py-20 lg:py-28 bg-gradient-to-br from-slate-100 via-white to-slate-50 relative">
          <div className="container mx-auto px-4 lg:px-6 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Why Choose GeniusNexa?
              </h2>
              <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                We combine cutting-edge technology with proven expertise to
                deliver solutions that drive real business results.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
              <div className="text-center group">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-[#E5C558] to-[#d4b54d] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <FontAwesomeIcon
                    icon={faUsers}
                    className="text-white text-2xl"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Expert Team
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Our certified professionals bring years of industry experience
                  and cutting-edge expertise to every project.
                </p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <FontAwesomeIcon
                    icon={faClock}
                    className="text-white text-2xl"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Rapid Delivery
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Agile methodologies and streamlined processes ensure fast
                  turnaround times without compromising quality.
                </p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <FontAwesomeIcon
                    icon={faGlobe}
                    className="text-white text-2xl"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Global Support
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  24/7 support across multiple time zones ensures your
                  operations run smoothly around the clock.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 lg:py-28 bg-gradient-to-r from-[#0b1220] via-[#1a2332] to-[#0b1220] relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <defs>
                <pattern
                  id="cta-grid"
                  width="10"
                  height="10"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 10 0 L 0 0 0 10"
                    fill="none"
                    stroke="#E5C558"
                    strokeWidth="0.5"
                  />
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#cta-grid)" />
            </svg>
          </div>

          <div className="container mx-auto px-4 lg:px-6 text-center relative z-10">
            <div className="max-w-4xl mx-auto space-y-8">
              <h2 className="text-3xl lg:text-5xl font-bold text-white leading-tight">
                Ready to Transform
                <span className="block text-[#E5C558] mt-2">
                  Your Business?
                </span>
              </h2>

              <p className="text-xl lg:text-2xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
                Let's discuss how our expert services can drive your business
                forward. Get a free consultation and personalized strategy.
              </p>

              <div className="flex flex-col sm:flex-row gap-6 justify-center pt-8">
                <Link
                  to="/contact-us"
                  className="group inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-[#0b1220] font-bold text-lg rounded-full hover:shadow-2xl hover:shadow-[#E5C558]/50 transition-all duration-300 hover:scale-105 focus:ring-4 focus:ring-[#E5C558]/30"
                  onClick={() =>
                    trackEvent("CTA", "ContactCTA", "ServicesMaster")
                  }
                  aria-label="Contact us for free consultation"
                >
                  Get Free Consultation
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="ml-3 group-hover:translate-x-1 transition-transform"
                  />
                </Link>

                <a
                  href="tel:+919944544603"
                  className="group inline-flex items-center justify-center px-10 py-5 bg-transparent border-2 border-[#E5C558] text-[#E5C558] font-bold text-lg rounded-full hover:bg-[#E5C558] hover:text-[#0b1220] transition-all duration-300 focus:ring-4 focus:ring-[#E5C558]/30"
                  aria-label="Call us directly"
                >
                  Call Now: +91 9944544603
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="ml-3 group-hover:translate-x-1 transition-transform"
                  />
                </a>
              </div>

              {/* Contact Info */}
              <div className="pt-12 border-t border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-gray-300">
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-4 bg-[#E5C558]/20 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-[#E5C558]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="text-sm text-gray-400 mb-1">Email Us</div>
                    <div className="font-semibold">support@geniusnexa.com</div>
                  </div>

                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-4 bg-[#E5C558]/20 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon
                        icon={faClock}
                        className="text-[#E5C558]"
                      />
                    </div>
                    <div className="text-sm text-gray-400 mb-1">
                      Support Hours
                    </div>
                    <div className="font-semibold">24/7 Available</div>
                  </div>

                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-4 bg-[#E5C558]/20 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon
                        icon={faGlobe}
                        className="text-[#E5C558]"
                      />
                    </div>
                    <div className="text-sm text-gray-400 mb-1">Coverage</div>
                    <div className="font-semibold">Global Reach</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServicesMaster;
